import { useEffect, useMemo, useState, type ReactNode } from "react";
import { usePermission } from "../../auth/access";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CastOutlinedIcon from "@mui/icons-material/CastOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import RadioOutlinedIcon from "@mui/icons-material/RadioOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  isCarePremise,
  type PremiseConfig,
  type SchemeKnobValues,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { hasPromotionsLicense, hasStreamingTvLicense } from "@shared/products";
import { withGlobalApps } from "@shared/app-merge";
import { withGlobalStations } from "@shared/radio-merge";
import { foyerApi, type RadioStationRecord } from "../../http/foyer";
import LivePreview from "../../components/template/LivePreview";
import { useTemplateApps } from "../../components/template/TemplateSubEditor";
import { useCommitNext } from "../../components/template/EditSection";
import type { PremiseCommit } from "../../context/PremiseWorkspace";
import { resolveOrders } from "../../components/template/screensModel";
import { PageHeader } from "../../components/PageHeader";
import MenuItemDrawer from "../../components/startnav/MenuItemDrawer";
import {
  INFO_STANDARD_PATH,
  TV_ITEM,
  buildMenuItems,
  effectiveBinding,
  reorderSubset,
  screenIdOf,
  type MenuItem,
  type MenuStatus,
} from "../../components/startnav/menuItems";
import { tokens } from "../../theme";

const ICONS: Record<string, ReactNode> = {
  [TV_ITEM]: <TvOutlinedIcon />,
  apps: <AppsOutlinedIcon />,
  info: <InfoOutlinedIcon />,
  radio: <RadioOutlinedIcon />,
  custom: <WebOutlinedIcon />,
  vod: <MovieOutlinedIcon />,
  guide: <LiveTvOutlinedIcon />,
  cast: <CastOutlinedIcon />,
  offers: <CampaignOutlinedIcon />,
};
const iconFor = (id: string) => ICONS[id] ?? <WidgetsOutlinedIcon />;

const STATUS = {
  active: { label: "startNavLayout.active", color: tokens.success },
  off: { label: "common:labels.off", color: tokens.inkFaint },
  hidden: {
    label: "startViewPage.hidden",
    color: tokens.warning,
    help: "startViewPage.hiddenHelp",
  },
} as const satisfies Record<
  MenuStatus,
  { label: string; color: string; help?: string }
>;

/** Titles the preview room may watch, summed across its rails - the TV's
 *  `vod` count. Undefined when the answer is not a catalogue. */
function vodTitleCount(res: unknown): number | undefined {
  const rails = (res as { rails?: unknown } | null)?.rails;
  if (!Array.isArray(rails)) return undefined;
  return rails.reduce<number>((n, r) => {
    const items = (r as { items?: unknown } | null)?.items;
    return n + (Array.isArray(items) ? items.length : 0);
  }, 0);
}

/**
 * Start & navigation → Start view ("Startvy", NendaPortal_1 1667:12421).
 *
 * The TV's start-screen menu as a table: TV first and fixed, then every item
 * the template offers in the order the TV draws them, each with its title, its
 * content and whether a guest actually sees it. Drag to reorder (saved at once
 * and toasted); click a row for its drawer, whose Save writes that item.
 * Information's drawer summarises its sections and links on to their two pages.
 *
 * A place with no template chosen is edited as the default one (Nord for a
 * hotel) - that is what its televisions show.
 */
export default function StartViewPage({
  premise,
  onCommit,
}: {
  premise: PremiseConfig;
  /** Saves a change to the premise and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const binding = useMemo(() => effectiveBinding(premise), [premise]);
  const effective = useMemo<PremiseConfig>(
    () =>
      premise.scheme === binding ? premise : { ...premise, scheme: binding },
    [premise, binding]
  );
  const manifest = SCHEME_MANIFESTS[binding.key];
  const apps = useTemplateApps(effective);
  const commitNext = useCommitNext(premise, onCommit);
  const canEditLayout = usePermission("tv-layout", "edit");

  const [stations, setStations] = useState<RadioStationRecord[] | null>(null);
  useEffect(() => {
    let alive = true;
    foyerApi
      .listRadioStations()
      .then((list) => alive && setStations(Array.isArray(list) ? list : []))
      .catch(() => alive && setStations(null));
    return () => {
      alive = false;
    };
  }, []);

  const [vodTitles, setVodTitles] = useState<number | undefined>();
  useEffect(() => {
    let alive = true;
    setVodTitles(undefined);
    if (!premise.premiseId) return;
    foyerApi
      .tvPreview(premise.premiseId)
      .then((res) => alive && setVodTitles(vodTitleCount(res)))
      .catch(() => {
        /* Unknown, not empty: the row says so rather than "hidden". */
      });
    return () => {
      alive = false;
    };
  }, [premise.premiseId]);

  const content = binding.content;
  const isCare = isCarePremise({
    type: premise.type,
    segment: premise.segment,
  });
  const model = manifest
    ? buildMenuItems(
        {
          manifest,
          knobs: binding.knobs,
          content,
          counts: {
            // The same merge the live preview (and the served config) applies.
            apps:
              apps.apps === null
                ? undefined
                : withGlobalApps(
                    apps.apps,
                    content.disabledAppIds,
                    content.appOrder,
                    isCare
                  ).length,
            stations:
              stations === null
                ? undefined
                : withGlobalStations(
                    stations,
                    content.stations,
                    content.disabledStationIds,
                    content.stationOrder
                  ).length,
            vodTitles,
          },
          tvPlayer: premise.tvPlayer,
          hasCoordinates:
            premise.address?.lat != null && premise.address?.lng != null,
          hasPromotions: hasPromotionsLicense(premise.products),
          hasStreamingTv: hasStreamingTvLicense(premise.products),
          appsCertified: apps.certifiedOn.size > 0,
          certificationNote: apps.certNote,
        },
        t
      )
    : undefined;

  if (!manifest || !model) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3 } }}>
        <PageHeader title={t("startViewPage.title")} />
        <Typography variant="body2" color="text.secondary">
          {t("startViewPage.unknownTemplate", { key: binding.key })}
        </Typography>
      </Box>
    );
  }

  const { items, infoRows, homeOrderKnobId } = model;
  const shown = items.filter((i) => i.status === "active").length;

  const where = premise.premiseName;
  const setKnobs = (next: SchemeKnobValues) =>
    commitNext(
      { ...premise, scheme: { ...binding, knobs: next } },
      {
        success: t("startViewPage.orderSaved", { name: where }),
        failure: "saveStartViewOrderFor",
        failureParams: { name: where },
      }
    );
  const reorder = (ids: string[]) => {
    if (!homeOrderKnobId || !canEditLayout) return;
    const knobs = binding.knobs ?? {};
    const full = resolveOrders(manifest, knobs)[homeOrderKnobId] ?? [];
    void setKnobs({ ...knobs, [homeOrderKnobId]: reorderSubset(full, ids) });
  };

  // `?item=` opens a menu item's drawer. Old links to a screen filed under
  // Information (`?item=wifi`) land on Information's standard-sections page
  // with that section selected.
  const requested = params.get("item") ?? "";
  if (infoRows.some((r) => screenIdOf(manifest, r) === requested)) {
    return (
      <Navigate
        to={`${INFO_STANDARD_PATH}?item=${encodeURIComponent(requested)}`}
        replace
      />
    );
  }
  const openItem = items.find((i) => i.id === requested) ?? null;
  const setItem = (id: string | null) =>
    setParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        if (id) q.set("item", id);
        else q.delete("item");
        return q;
      },
      { replace: true }
    );
  const open = (id: string) => setItem(id);

  return (
    <Box
      sx={{ px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3 } }}
      data-testid="start-view"
    >
      <PageHeader
        title={t("startViewPage.title")}
        subtitle={t("startViewPage.subtitle")}
      />

      <Box
        component="section"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" },
          columnGap: 7,
          rowGap: 2,
          mb: 8,
        }}
      >
        <SectionHeading
          title={t("startNavLayout.preview")}
          text={t("startViewPage.previewText")}
        />
        <LivePreview premise={effective} maxWidth={824} hideLabel />
      </Box>

      <Box component="section">
        <SectionHeading
          title={t("startViewPage.menuItemsShown", { count: shown })}
          text={t("startViewPage.menuItemsText")}
        />
        <Box sx={{ mt: 3 }}>
          <MenuTable items={items} onReorder={reorder} onOpen={open} />
        </Box>
      </Box>

      <MenuItemDrawer
        item={openItem}
        premise={effective}
        icon={openItem ? iconFor(openItem.id) : undefined}
        apps={apps}
        onClose={() => setItem(null)}
        onSave={async (next) => {
          const name =
            openItem?.title ||
            openItem?.name ||
            t("startViewPage.itemFallback");
          const ok = await commitNext(next, {
            success: t("startViewPage.itemUpdated", { name }),
            failure: "saveStartViewItem",
            failureParams: { name },
          });
          if (ok) setItem(null);
          return ok;
        }}
        onOpenPage={async (next, to) => {
          // Leaving for the item's own page: keep what was typed by saving it
          // first (a failure has toasted and keeps the drawer open).
          const name =
            openItem?.title ||
            openItem?.name ||
            t("startViewPage.itemFallback");
          const ok = await commitNext(next, {
            success: t("startViewPage.itemUpdated", { name }),
            failure: "saveStartViewItem",
            failureParams: { name },
          });
          if (ok) navigate(to);
        }}
      />
    </Box>
  );
}

function SectionHeading({ title, text }: { title: string; text: string }) {
  return (
    <Box>
      <Typography
        component="h2"
        sx={{ typography: "titleLg", fontWeight: "fontWeightSemibold" }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: tokens.inkMuted }}>
        {text}
      </Typography>
    </Box>
  );
}

/** Present to a screen reader, invisible on screen. */
const VISUALLY_HIDDEN = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

/** Hidden column headers still name their column for a screen reader. */
function HiddenHead({ children }: { children: string }) {
  return (
    <TableCell>
      <Box component="span" sx={VISUALLY_HIDDEN}>
        {children}
      </Box>
    </TableCell>
  );
}

/** Title and Content fold into the item cell below `md`. */
const WIDE = { display: { xs: "none", md: "table-cell" } };

function MenuTable({
  items,
  onReorder,
  onOpen,
}: {
  items: MenuItem[];
  onReorder: (ids: string[]) => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const orderable = items.filter((i) => i.orderable).map((i) => i.id);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = orderable.indexOf(String(active.id));
    const to = orderable.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = orderable.slice();
    next.splice(from, 1);
    next.splice(to, 0, String(active.id));
    onReorder(next);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <Table
          aria-label={t("startViewPage.tableAria")}
          data-testid="start-view-table"
          sx={{
            "& .MuiTableCell-root": {
              borderColor: tokens.divider,
              fontSize: 14,
              py: 1.5,
            },
            "& .MuiTableCell-head": {
              fontWeight: "fontWeightSemibold",
              color: tokens.ink,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <HiddenHead>{t("startViewPage.columns.reorder")}</HiddenHead>
              <TableCell sx={{ width: 64 }}>
                {t("startViewPage.columns.order")}
              </TableCell>
              <HiddenHead>{t("startViewPage.columns.menuItem")}</HiddenHead>
              <TableCell sx={WIDE}>{t("common:labels.title")}</TableCell>
              <TableCell sx={WIDE}>{t("common:labels.content")}</TableCell>
              <TableCell>{t("common:labels.status")}</TableCell>
              <HiddenHead>{t("startViewPage.columns.manage")}</HiddenHead>
            </TableRow>
          </TableHead>
          <SortableContext
            items={orderable}
            strategy={verticalListSortingStrategy}
          >
            <TableBody>
              {items.map((item, i) => (
                <MenuRow
                  key={item.id}
                  item={item}
                  position={i + 1}
                  onOpen={() => onOpen(item.id)}
                />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
      </DndContext>
    </Box>
  );
}

function MenuRow({
  item,
  position,
  onOpen,
}: {
  item: MenuItem;
  position: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const s = useSortable({ id: item.id, disabled: !item.orderable });
  const status = STATUS[item.status];
  const statusHelp = "help" in status ? t(status.help) : "";
  const contentText =
    item.content === t("menuItems.noManageableContent") ? (
      <Tooltip title={t("startViewPage.noManageableTooltip")}>
        <span>{item.content}</span>
      </Tooltip>
    ) : (
      item.content
    );
  return (
    <TableRow
      ref={s.setNodeRef}
      hover
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      data-testid={`start-view-row-${item.id}`}
      style={{
        transform: CSS.Translate.toString(s.transform),
        transition: s.transition,
        opacity: s.isDragging ? 0.6 : undefined,
        position: "relative",
        zIndex: s.isDragging ? 1 : undefined,
      }}
      sx={{
        cursor: "pointer",
        bgcolor: tokens.surface,
        "&:last-of-type .MuiTableCell-root": { borderBottom: 0 },
        "&:focus-visible": {
          outline: `2px solid ${tokens.brand}`,
          outlineOffset: -2,
        },
      }}
    >
      <TableCell
        sx={{ width: 48, pr: 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {item.orderable && (
          <Box
            {...s.attributes}
            {...s.listeners}
            aria-label={t("startNavLayout.drag", { name: item.name })}
            sx={{ display: "flex", color: tokens.inkFaint, cursor: "grab" }}
            data-testid={`start-view-drag-${item.id}`}
          >
            <DragIndicatorIcon />
          </Box>
        )}
      </TableCell>
      <TableCell data-testid={`start-view-order-${item.id}`}>
        {position}
      </TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: "flex", color: tokens.ink }} aria-hidden>
            {iconFor(item.id)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: "fontWeightSemibold" }}
            >
              {item.name}
            </Typography>
            {/* The Title and Content columns, folded in on a narrow screen. */}
            <Typography
              variant="bodySm"
              sx={{
                display: { xs: "block", md: "none" },
                color: tokens.inkMuted,
              }}
            >
              {item.content}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={WIDE} data-testid={`start-view-title-${item.id}`}>
        {item.title}
      </TableCell>
      <TableCell
        sx={{ ...WIDE, color: tokens.inkMuted }}
        data-testid={`start-view-content-${item.id}`}
      >
        {contentText}
      </TableCell>
      <TableCell data-testid={`start-view-status-${item.id}`}>
        <Tooltip title={statusHelp}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: status.color,
              }}
            />
            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
              {t(status.label)}
            </Typography>
          </Stack>
        </Tooltip>
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          aria-label={t("startViewPage.manageNamed", { name: item.name })}
          sx={{ textTransform: "none", fontWeight: "fontWeightSemibold" }}
          data-testid={`start-view-manage-${item.id}`}
        >
          {t("startViewPage.columns.manage")}
        </Button>
      </TableCell>
    </TableRow>
  );
}
